// AppRoutes.tsx - Main routing component
import React, { Suspense } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { IonRouterOutlet } from "@ionic/react";
import { SocketProvider } from "./contexts/SocketContext";
import useAuth from "./hooks/useAuth";
import { BiometricAuthHandler } from "./routes/BiometricAuthHandler";
import { NavigationHandler } from "./routes/NavigationHandler";
import SessionRestoring from "./routes/SessionRestoring";
import SuspenseFallback from "./routes/SuspenseFallback";
import { peekReturnTo, rememberReturnTo } from "./routes/returnTo";
import { routes } from "./routes/routes";

const AppRoutes: React.FC = () => {
  const { user, isSessionRestored } = useAuth();

  // ── F-137 ──────────────────────────────────────────────────────────────────────────────
  // Do not answer "is this person signed in?" before the stored session has been read.
  //
  // What used to happen on `/journal` (or any deep link, or any reload): the first render
  // saw `user === null` because `Preferences.get("user")` had not resolved yet, matched the
  // `requiresAuth && !isAuthenticated` branch, and redirected to /signin. A tick later the
  // session restored, /signin saw an authenticated user, and bounced to /home. The requested
  // screen never got a turn — the redirect won the race against the router.
  //
  // `isSessionRestored` is the "not yet known" state made explicit, and it is a SEPARATE
  // flag from `isLoading` on purpose: `isLoading` also goes true during sign-in and profile
  // refreshes, so gating on it blanked the app mid-flow (which is why the old
  // `if (isLoading) return null` guard here was commented out rather than fixed).
  //
  // While it is false the app paints a waiting state — not /home, and not a sign-in prompt.
  if (!isSessionRestored) return <SessionRestoring />;

  return (
    <SocketProvider
      defaultUser={
        user
          ? {
              profile_id: user.profile_id,
              username: user.username || user.email || "",
            }
          : undefined
      }
      timeZone={user?.time_zone}
    >
      <BiometricAuthHandler />
      <NavigationHandler>
        <IonRouterOutlet>
          <Suspense fallback={<SuspenseFallback />}>
            <Switch>
              {routes.map(
                ({ path, component: Component, requiresAuth, redirectTo }) => {
                  if (redirectTo) {
                    return (
                      <Route key={path} exact path={path}>
                        <Redirect to={redirectTo} />
                      </Route>
                    );
                  }

                  return (
                    <Route
                      key={path}
                      exact
                      path={path}
                      render={({ location }) => {
                        const isAuthenticated = !!user;

                        // An already-signed-in user has no business on the sign-in screen.
                        // `peekReturnTo` (never `takeReturnTo`) because this runs in render
                        // and may run more than once — spending the destination here would
                        // make the answer depend on how often React re-rendered. The
                        // sign-in completion handlers spend it exactly once.
                        if (path === "/signin" && isAuthenticated) {
                          return <Redirect to={peekReturnTo()} />;
                        }

                        if (requiresAuth && !isAuthenticated) {
                          // Park the screen they asked for FIRST, so sign-in can hand it
                          // back instead of dropping them on /home. By now `user === null`
                          // is a real answer, not a not-yet-loaded one.
                          rememberReturnTo(location);
                          return <Redirect to="/signin?register=false" />;
                        }

                        return Component ? <Component /> : null;
                      }}
                    />
                  );
                }
              )}
              {/* Invitation deep-link entry point — token is captured by AppUrlListener */}
              <Route exact path="/signup">
                <Redirect to="/signin?register=false" />
              </Route>
              <Route path="*">
                <Redirect to="/home" />
              </Route>
            </Switch>
          </Suspense>
        </IonRouterOutlet>
      </NavigationHandler>
    </SocketProvider>
  );
};
export default AppRoutes;
